import { DestroyRef, inject, Injectable } from "@angular/core";
import {
  BehaviorSubject,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs";

import { EventBusService } from "events/event-bus.service";
import { OrgUpdatedEvent } from "events/org-updated.event";
import Organization, { OrganizationFields, OrganizationStats } from "models/Organization";
import { OrganizationService } from "services/organization.service";

import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UserFacade } from "./user.facade";

@Injectable({ providedIn: "root" })
export class OrgListFacade {
  private readonly orgService = inject(OrganizationService);
  private readonly userFacade = inject(UserFacade);
  private readonly eventBus = inject(EventBusService);
  private readonly destroyRef = inject(DestroyRef);

  //#region State
  private readonly _orgs = new BehaviorSubject<OrganizationStats[] | null | undefined>(
    undefined
  );

  public get orgs(): OrganizationStats[] | null | undefined {
    return this._orgs.value;
  }
  //#endregion State

  public constructor() {
    this.subscribeToOrgUpdatedEvent();
  }

  //#region Event bus subscriptions
  private subscribeToOrgUpdatedEvent(): void {
    this.eventBus
      .eventsFor(this)
      .ofClass(OrgUpdatedEvent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ updatedOrg }) => {
        this.updateOrg(updatedOrg);
      });
  }
  //#endregion Event bus subscriptions

  //#region Public API: Synchronous methods
  public updateOrg(updatedOrg: Organization): void {
    if (!this._orgs.value) return;
    this._orgs.next(
      this._orgs.value.map((org) =>
        org._id === updatedOrg._id ? { ...org, ...updatedOrg } : org
      )
    );
  }
  //#endregion Public API: Synchronous methods

  //#region Public API: Observable methods
  public fetchOrgs$(): Observable<OrganizationStats[] | null> {
    return of(undefined).pipe(
      withLatestFrom(this.userFacade.userHasDevPermissions$),
      switchMap(([_, userHasDevPermissions]) => {
        return userHasDevPermissions ? this.orgService.getOrganizations() : of(null);
      }),
      tap((orgs) => {
        this._orgs.next(orgs);
      })
    );
  }

  public createOrg$(orgData: OrganizationFields): Observable<Organization> {
    return of(orgData).pipe(
      switchMap((org) => this.orgService.addOrganization(org)),
      tap((newOrg) => {
        this._orgs.next([
          ...(this._orgs.value ?? []),
          {
            ...newOrg,
            isActive: true,
            reports: 0,
            queued: 0,
            processing: 0,
            failed: 0,
            spaceUsed: 0,
          } as OrganizationStats,
        ]);
      })
    );
  }

  public toggleOrgActiveStatus$(
    orgId: string,
    isActive: boolean
  ): Observable<Organization> {
    return of({ orgId, isActive }).pipe(
      switchMap(({ orgId, isActive }) =>
        this.orgService.updateOrganizationIsActive(orgId, isActive)
      ),
      tap((updatedOrg) => {
        this.updateOrg(updatedOrg);
      })
    );
  }

  public hardDeleteOrg$(
    orgId: string,
    confirmationPhraseInput: string,
    dryRun: boolean = true
  ): Observable<void> {
    return of({ orgId, confirmationPhraseInput, dryRun }).pipe(
      switchMap(({ orgId, confirmationPhraseInput, dryRun }) =>
        this.orgService.hardDeleteOrganization(orgId, confirmationPhraseInput, dryRun)
      ),
      tap(() => {
        if (!dryRun) {
          this._orgs.next((this._orgs.value ?? []).filter((org) => org._id !== orgId));
        }
      }),
      map(() => undefined)
    );
  }
  //#endregion Public API: Observable methods

  //#region Public API: Synchronous fire-and-forget methods
  public fetchOrgs(): void {
    this.fetchOrgs$().pipe(take(1)).subscribe();
  }

  public createOrg(orgData: OrganizationFields): void {
    this.createOrg$(orgData).pipe(take(1)).subscribe();
  }

  public toggleOrgActiveStatus(orgId: string, isActive: boolean): void {
    this.toggleOrgActiveStatus$(orgId, isActive).pipe(take(1)).subscribe();
  }
  //#endregion Public API: Synchronous fire-and-forget methods

  //#region Public API: Observable properties
  public readonly orgs$: Observable<OrganizationStats[] | null | undefined> =
    this.userFacade.user$.pipe(
      switchMap(() => this.fetchOrgs$()),
      switchMap(() => this._orgs.asObservable()),
      shareReplay(1)
    );

  public readonly activeOrgs$: Observable<OrganizationStats[] | null> = this.orgs$.pipe(
    map((orgs) => orgs?.filter((org) => org.isActive) ?? null)
  );
  //#endregion Public API: Observable properties
}
